import { useRef, useState, useCallback, ReactNode, CSSProperties } from "react";
import { motion, useReducedMotion, useSpring, useTransform } from "framer-motion";

type GlassIntensity = "soft" | "medium" | "strong";

interface SpotlightCardProps {
    children: ReactNode;
    className?: string;
    /** Enable 3D tilt effect on hover */
    enableTilt?: boolean;
    /** Maximum tilt angle in degrees */
    tiltAmount?: number;
    /** Spotlight color - defaults to theme-aware */
    spotlightColor?: string;
    /** Additional inline styles */
    style?: CSSProperties;
    /** Border radius - defaults to 24px */
    borderRadius?: string;
    /** Glass blur/shadow intensity tier */
    intensity?: GlassIntensity;
}

const intensityClasses: Record<GlassIntensity, string> = {
    soft: "glass-soft",
    medium: "glass-medium",
    strong: "glass-strong"
};

/**
 * Premium glass card with spotlight and optional tilt effects.
 * 
 * Features:
 * - Glass material (backdrop-blur + semi-transparent bg)
 * - Noise texture overlay
 * - Subtle border highlight
 * - Hover tint overlay
 * - Optional 3D parallax tilt (damped spring physics)
 * - Performance fallback for devices without backdrop-filter
 */
export const SpotlightCard = ({
    children,
    className = "",
    enableTilt = false,
    tiltAmount = 8,
    spotlightColor,
    style,
    borderRadius = "24px",
    intensity = "medium"
}: SpotlightCardProps) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const reducedMotion = useReducedMotion();
    const canTilt = enableTilt && !reducedMotion;

    // Spring physics for smooth tilt
    const springConfig = { stiffness: 150, damping: 20 };
    const rotateX = useSpring(0, springConfig);
    const rotateY = useSpring(0, springConfig);

    // Transform mouse position to rotation
    const tiltX = useTransform(rotateX, [-1, 1], [tiltAmount, -tiltAmount]);
    const tiltY = useTransform(rotateY, [-1, 1], [-tiltAmount, tiltAmount]);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (!cardRef.current) return;

            const rect = cardRef.current.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const y = (e.clientY - rect.top) / rect.height;

            if (canTilt) {
                // Normalize to -1 to 1 range
                rotateX.set((y - 0.5) * 2);
                rotateY.set((x - 0.5) * 2);
            }
        },
        [canTilt, rotateX, rotateY]
    );

    const handleMouseEnter = useCallback(() => {
        if (!reducedMotion) {
            setIsHovered(true);
        }
    }, [reducedMotion]);

    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
        if (canTilt) {
            rotateX.set(0);
            rotateY.set(0);
        }
    }, [canTilt, rotateX, rotateY]);

    // Default spotlight uses theme-aware violet/blue
    const defaultSpotlight = "rgba(var(--glass-spotlight-rgb, 139, 92, 246), var(--glass-spotlight-alpha, 0.16))";
    const spotlight = spotlightColor || defaultSpotlight;

    return (
        <motion.div
            ref={cardRef}
            className={`
                relative overflow-hidden
                glass-panel ${intensityClasses[intensity]} glass-noise glass-border-glow glass-blur-fallback
                ${className}
            `}
            style={{
                borderRadius,
                perspective: canTilt ? "1000px" : undefined,
                transformStyle: canTilt ? "preserve-3d" : undefined,
                rotateX: canTilt ? tiltX : undefined,
                rotateY: canTilt ? tiltY : undefined,
                transitionTimingFunction: "var(--glass-ease-standard)",
                ...style
            }}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Spotlight Effect Layer */}
            <div
                className="absolute inset-0 pointer-events-none transition-opacity duration-300"
                style={{
                    opacity: isHovered ? 1 : 0,
                    backgroundColor: spotlight,
                    zIndex: 0
                }}
            />

            {/* Content Layer */}
            <div className="relative" style={{ zIndex: 2 }}>
                {children}
            </div>
        </motion.div>
    );
};

export default SpotlightCard;



